import { useState } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart2, Eye, Users, AlertCircle, UserRound, UserRoundCheck, Repeat2, Clock, MousePointerClick, TrendingUp, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { useTeacherProfile } from "@/contexts/TeacherProfileContext";
import { useAnalyticsInsights } from "@/hooks/use-analytics";
import { UnifiedCard } from "@/components/teacher/analytics/UnifiedCard";
import { KpiTile, type KpiTileProps } from "@/components/teacher/analytics/KpiTile";
import { TierHero } from "@/components/teacher/analytics/TierHero";
import { UnreadInquiriesCallout } from "@/components/teacher/analytics/UnreadInquiriesCallout";
import { BenchmarkCharts } from "@/components/teacher/analytics/BenchmarkCharts";
import { BenchmarksUpsellCard, CohortTooSmallNotice } from "@/components/teacher/analytics/BenchmarksAccessNotice";
import { isReleaseFlagOn } from "@/config/featureFlags";
import { cn } from "@/lib/utils";
import type {
  AnalyticsSummaryResponse,
  OfferingTypeMetrics,
  BenchmarkResponse,
  OfferingBreakdownResponse,
  StudentEngagementResponse,
} from "@/services/analyticsService";

// ── Formatting helpers ────────────────────────────────────────────────────────

const fmt = (n: number) => n.toLocaleString();
const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
function mins(n: number | null): string {
  if (n == null) return "—";
  if (n < 60) return `${n}m`;
  const d = Math.floor(n / 1440);
  const h = Math.floor((n % 1440) / 60);
  const m = n % 60;
  const parts: string[] = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  return parts.join(" ");
}
const ctrOf = (m: OfferingTypeMetrics) =>
  m.totalImpressions === 0 ? "N/A" : `${(m.clickThroughRate * 100).toFixed(1)}%`;

// ── Offering type selection ───────────────────────────────────────────────────

export type OfferingTypeKey = "ONE_TO_ONE" | "GROUP";

const OFFERING_TYPE_LABELS: Record<OfferingTypeKey, string> = {
  ONE_TO_ONE: "1-on-1",
  GROUP: "Group",
};

/**
 * D5 (locked design decision): the INITIAL offering-type selection is whichever type
 * has the higher `totalImpressions + totalInquiries` in the current 30-day window, with
 * a tie-break to ONE_TO_ONE. Only the initial selection follows this rule — once the
 * teacher toggles manually their choice wins for the rest of the page's lifetime, which
 * is why this is used as a `useState` lazy initializer and never recomputed on render.
 */
function computeDefaultOfferingType(summary: AnalyticsSummaryResponse): OfferingTypeKey {
  const reach = (m: OfferingTypeMetrics | null) => (m ? m.totalImpressions + m.totalInquiries : -1);
  return reach(summary.group) > reach(summary.oneToOne) ? "GROUP" : "ONE_TO_ONE";
}

/**
 * AC#6 cohort medians are published per `(subject, offeringType)` pair, while
 * `summary.oneToOne` / `summary.group` are aggregates across ALL of the teacher's
 * subjects for that type. A clean match therefore only exists when the teacher has
 * exactly ONE benchmarked subject for the selected type — with zero or several we
 * return null and simply omit the inline median rather than averaging medians across
 * different subjects (the full per-subject picture stays visible in the charts below).
 */
function findSoleBenchmark(
  benchmarks: BenchmarkResponse[],
  offeringType: OfferingTypeKey
): BenchmarkResponse | null {
  const scoped = benchmarks.filter((b) => b.offeringType === offeringType);
  return scoped.length === 1 ? scoped[0] : null;
}

// ── KPI builders ──────────────────────────────────────────────────────────────

/**
 * Headline KPIs (AC#2 — "the two headline numbers (inquiries received, response
 * rate)"). `totalInquiries` uses the LOCKED wording table documented in `KpiTile.tsx`
 * verbatim; response rate has no locked row (the table only covers the 4 backend
 * fields), so its wording is written in the same plain-language voice.
 */
function buildHeadlineKpis(
  m: OfferingTypeMetrics,
  cohort: BenchmarkResponse | null
): [KpiTileProps, KpiTileProps] {
  // `trend` is passed unconditionally on purpose. Period-over-period is a Gold+ entitlement
  // (ADR-140) enforced SERVER-side: below Gold the API withholds `prior` entirely, and
  // TrendIndicator renders nothing for a null prior, so this page shows no comparison without
  // needing a check of its own.
  //
  // Do not "fix" this by adding `summary.periodComparisonVisible` here. With the release flag off
  // the server still returns `prior` to every tier -- its pre-feature behaviour -- and this page
  // has always shown trends to everyone, so a client-side gate would remove them while the flag is
  // off. That is exactly the inertness a release flag exists to guarantee. The dashboard card is
  // different and does combine the two: it showed trends to nobody before the flag.
  return [
    {
      label: "Sent you an inquiry",
      subcaption: "students who reached out",
      value: fmt(m.totalInquiries),
      trend: { current: m.totalInquiries, prior: m.prior?.totalInquiries ?? null },
      cohortMedian: cohort ? `Cohort median ${fmt(cohort.cohortMedianInquiryVolume)}` : undefined,
    },
    {
      label: "How often you reply",
      subcaption: "of students who reached out",
      value: pct(m.responseRate),
      trend: { current: m.responseRate, prior: m.prior?.responseRate ?? null },
      cohortMedian: cohort ? `Cohort median ${pct(cohort.cohortMedianResponseRate)}` : undefined,
    },
  ];
}

/** Placeholder headline tiles for the crash-course-only case — `TierHero` always needs
 *  its two tiles, but there is no per-type metrics object to populate them from, so the
 *  explanatory sentence inside `TierHero` carries the real meaning here. */
const CRASH_COURSE_PLACEHOLDER_KPIS: [KpiTileProps, KpiTileProps] = [
  { label: "Sent you an inquiry", subcaption: "students who reached out", value: "—" },
  { label: "How often you reply", subcaption: "of students who reached out", value: "—" },
];

/** The remaining locked-table metrics, shown below the hero. Only `clickThroughRate`
 *  has a matching cohort-median field in `BenchmarkResponse` — impressions and reply
 *  time have none, so they never carry a `cohortMedian`. */
function buildSecondaryKpis(m: OfferingTypeMetrics, cohort: BenchmarkResponse | null): KpiTileProps[] {
  return [
    {
      label: "Times students saw you",
      subcaption: "in search results",
      value: fmt(m.totalImpressions),
    },
    {
      label: "Turned into profile views",
      subcaption: "out of everyone who saw you",
      value: ctrOf(m),
      cohortMedian: cohort ? `Cohort median ${pct(cohort.cohortMedianClickThroughRate)}` : undefined,
    },
    {
      label: "Your typical reply time",
      subcaption: "from their first message",
      value: mins(m.avgResponseTimeMinutes),
    },
  ];
}

// ── OfferingTypeToggle ────────────────────────────────────────────────────────

function OfferingTypeToggle({ selected, onSelect }: { selected: OfferingTypeKey; onSelect: (type: OfferingTypeKey) => void }) {
  return (
    <div role="group" aria-label="Offering type" className="inline-flex items-center gap-0.5 rounded-xl bg-slate-100 p-1">
      {(["ONE_TO_ONE", "GROUP"] as const).map((type) => {
        const isActive = selected === type;
        return (
          <button
            key={type}
            type="button"
            aria-pressed={isActive}
            onClick={() => onSelect(type)}
            className={cn(
              "rounded-lg px-4 py-1.5 text-sm font-semibold transition-all duration-200 cursor-pointer",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
              isActive ? "bg-indigo-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
            )}
          >
            {OFFERING_TYPE_LABELS[type]}
          </button>
        );
      })}
    </div>
  );
}

// ── SecondaryKpiGrid ──────────────────────────────────────────────────────────

function SecondaryKpiGrid({ metrics, cohort }: { metrics: OfferingTypeMetrics; cohort: BenchmarkResponse | null }) {
  const items = [
    {
      Icon: Eye,
      iconBg: "bg-indigo-50",
      iconColor: "text-indigo-500",
      label: "Times students saw you",
      subcaption: "in search results",
      value: fmt(metrics.totalImpressions),
      extra: undefined as string | undefined,
    },
    {
      Icon: MousePointerClick,
      iconBg: "bg-violet-50",
      iconColor: "text-violet-500",
      label: "Turned into profile views",
      subcaption: "out of everyone who saw you",
      value: ctrOf(metrics),
      extra: cohort ? `Cohort median ${(cohort.cohortMedianClickThroughRate * 100).toFixed(1)}%` : undefined,
    },
    {
      Icon: Clock,
      iconBg: "bg-amber-50",
      iconColor: "text-amber-500",
      label: "Your typical reply time",
      subcaption: "from their first message",
      value: mins(metrics.avgResponseTimeMinutes),
      extra: undefined,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {items.map((item) => (
        <div key={item.label} className="rounded-2xl bg-white shadow-md p-5 flex items-start gap-4">
          <div className={cn("w-11 h-11 rounded-xl flex items-center justify-center shrink-0", item.iconBg)}>
            <item.Icon className={cn("w-5 h-5", item.iconColor)} aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-600 leading-snug">{item.label}</p>
            <p className="text-xs text-slate-400 mt-0.5">{item.subcaption}</p>
            <p className="text-2xl font-bold text-slate-900 mt-2 tracking-tight tabular-nums">{item.value}</p>
            {item.extra && <p className="text-xs text-slate-400 mt-1">{item.extra}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── AnalyticsKpiSection (replaces AnalyticsSummaryCards) ──────────────────────

function AnalyticsKpiSection({
  summary,
  benchmarks,
  selectedOfferingType,
  onSelectOfferingType,
  showToggle,
  isCrashCourseOnly,
}: {
  summary: AnalyticsSummaryResponse;
  benchmarks: BenchmarkResponse[];
  selectedOfferingType: OfferingTypeKey;
  onSelectOfferingType: (type: OfferingTypeKey) => void;
  showToggle: boolean;
  isCrashCourseOnly: boolean;
}) {
  const metrics = selectedOfferingType === "ONE_TO_ONE" ? summary.oneToOne : summary.group;
  const cohort = findSoleBenchmark(benchmarks, selectedOfferingType);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="bg-white rounded-xl px-3 py-2 shadow-sm inline-flex items-center gap-2.5">
          <CalendarDays className="w-5 h-5 text-slate-400" aria-hidden="true" />
          <h2 className="text-base font-bold text-slate-800">Performance</h2>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100">
            Last 30 days
          </span>
        </div>
        {showToggle && <OfferingTypeToggle selected={selectedOfferingType} onSelect={onSelectOfferingType} />}
      </div>

      <TierHero
        tier={summary.tier}
        cohortSubject={cohort?.subject}
        cohortMinimumMet={summary.cohortMinimumMet}
        isCrashCourseOnly={isCrashCourseOnly}
        headlineKpis={metrics ? buildHeadlineKpis(metrics, cohort) : CRASH_COURSE_PLACEHOLDER_KPIS}
      />

      {metrics && <SecondaryKpiGrid metrics={metrics} cohort={cohort} />}
    </section>
  );
}

// ── OfferingBreakdownTable ────────────────────────────────────────────────────

const OFFERING_TYPE_BADGES: Record<string, { label: string; cls: string; Icon: typeof UserRound }> = {
  ONE_TO_ONE:   { label: "1-on-1",       cls: "bg-indigo-50 text-indigo-700 border-indigo-200", Icon: UserRound },
  GROUP:        { label: "Group",        cls: "bg-violet-50 text-violet-700 border-violet-200", Icon: Users },
  CRASH_COURSE: { label: "Crash Course", cls: "bg-amber-50  text-amber-700  border-amber-200",  Icon: BarChart2 },
};

function OfferingBreakdownTable({ offerings }: { offerings: OfferingBreakdownResponse[] }) {
  if (offerings.length === 0) return null;

  return (
    <section>
      <div className="flex items-center gap-2 mb-3 px-0.5">
        <div className="bg-white rounded-xl px-3 py-2 shadow-sm inline-flex items-center gap-2">
          <Eye className="w-5 h-5 text-slate-400" aria-hidden="true" />
          <h2 className="text-base font-bold text-slate-800">Offering Breakdown</h2>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
            {offerings.length}
          </span>
        </div>
      </div>
      <div className="rounded-2xl bg-white shadow-md overflow-hidden divide-y divide-slate-50">
        {offerings.map((o) => {
          const badge = OFFERING_TYPE_BADGES[o.offeringType] ?? { label: o.offeringType.replace(/_/g, "-"), cls: "bg-slate-50 text-slate-600 border-slate-200", Icon: BarChart2 };
          const BadgeIcon = badge.Icon;
          const ctrPct = o.impressions > 0 ? o.clickThroughRate * 100 : 0;
          const ctrColorText = ctrPct >= 10 ? "text-emerald-600" : ctrPct >= 5 ? "text-amber-600" : "text-slate-500";
          const ctrColorBar  = ctrPct >= 10 ? "bg-emerald-500" : ctrPct >= 5 ? "bg-amber-400" : "bg-slate-300";
          return (
            <div key={o.offeringId} className="px-5 py-5">
              <div className="flex items-center gap-2.5 mb-3">
                <span className={cn("inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border shrink-0", badge.cls)}>
                  <BadgeIcon className="w-3 h-3" aria-hidden="true" />
                  {badge.label}
                </span>
                <p className="text-sm font-semibold text-slate-800 truncate">{o.offeringTitle}</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-400 mb-1">Views</p>
                  <p className="text-lg font-bold text-slate-800 tabular-nums">{o.impressions.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">Clicks</p>
                  <p className="text-lg font-bold text-slate-800 tabular-nums">{o.clicks.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">Click-through</p>
                  <p className={cn("text-lg font-bold tabular-nums", ctrColorText)}>
                    {o.impressions === 0 ? "N/A" : `${ctrPct.toFixed(1)}%`}
                  </p>
                  {o.impressions > 0 && (
                    <div className="w-full h-1.5 rounded-full bg-slate-100 mt-1.5 overflow-hidden">
                      <div
                        className={cn("h-full rounded-full transition-all duration-500", ctrColorBar)}
                        style={{ width: `${Math.min(ctrPct * 5, 100)}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── StudentEngagementCard ─────────────────────────────────────────────────────

function StudentEngagementCard({ engagement }: { engagement: StudentEngagementResponse }) {
  const repeatPct = Math.round(engagement.repeatRate * 100);
  return (
    <section>
      <div className="flex items-center gap-2 mb-3 px-0.5">
        <div className="bg-white rounded-xl px-3 py-2 shadow-sm inline-flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-slate-400" aria-hidden="true" />
          <h2 className="text-base font-bold text-slate-800">Student Engagement</h2>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-white shadow-md p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
            <UserRound className="w-5 h-5 text-indigo-500" aria-hidden="true" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Unique students</p>
            <p className="text-2xl font-bold text-slate-900 tabular-nums">{fmt(engagement.uniqueStudents)}</p>
            <p className="text-xs text-slate-400 mt-0.5">reached out this month</p>
          </div>
        </div>

        <div className="rounded-2xl bg-white shadow-md p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
            <Repeat2 className="w-5 h-5 text-violet-500" aria-hidden="true" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Repeat students</p>
            <p className="text-2xl font-bold text-slate-900 tabular-nums">{fmt(engagement.repeatStudents)}</p>
            <p className="text-xs text-slate-400 mt-0.5">came back for more</p>
          </div>
        </div>

        <div className="rounded-2xl bg-white shadow-md p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
              <UserRoundCheck className="w-5 h-5 text-emerald-500" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Repeat rate</p>
              <p className="text-2xl font-bold text-slate-900 tabular-nums">{pct(engagement.repeatRate)}</p>
            </div>
          </div>
          <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-amber-500 transition-all duration-700"
              style={{ width: `${Math.min(repeatPct, 100)}%` }}
              role="progressbar"
              aria-valuenow={repeatPct}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          <p className="text-xs text-slate-400 mt-2">of students who returned</p>
        </div>
      </div>
    </section>
  );
}

// ── TeacherAnalyticsPage ──────────────────────────────────────────────────────

function PageShell({ children }: { children: React.ReactNode }) {
  const { teacherProfile } = useTeacherProfile();

  const teacherName    = teacherProfile?.fullName || "";
  const teacherAvatar  = teacherProfile?.profileImage || "";
  const teacherSubjects: string[] = (teacherProfile?.subjects || [])
    .map((s: any) => typeof s === "string" ? s : s?.subjectName || s?.subject?.name || s?.name || "")
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white teacher-theme">
      <SidebarProvider>
        <DashboardTopNav role="teacher" />
        <div className="min-h-screen flex w-full pt-14">
          <TeacherSidebar
            teacherName={teacherName}
            teacherAvatar={teacherAvatar}
            subjects={teacherSubjects}
          />

          <div className="flex-1 flex flex-col min-w-0">
            {/* Consistent page header — lives outside padded main so border-b runs edge-to-edge */}
            <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100 shrink-0">
              <h1 className="text-2xl font-bold text-slate-900">Analytics &amp; Insights</h1>
              <p className="text-sm text-slate-500">How your offerings are performing · Last 30 days</p>
            </div>
            <main className="flex-1 p-4 md:p-6 overflow-auto bg-slate-50">
              {children}
            </main>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
}

export default function TeacherAnalyticsPage() {
  usePageTitle('Analytics | BookMyCoaching');
  const { data, isLoading, isError, refetch } = useAnalyticsInsights();

  // Selection is lifted here (not inside the KPI section) because the benchmark charts
  // read the same choice. `null` means "not chosen yet" — the D5 default is derived from
  // the loaded summary below without overwriting a manual choice on later renders.
  const [pickedOfferingType, setPickedOfferingType] = useState<OfferingTypeKey | null>(null);

  if (isLoading) {
    return (
      <PageShell>
        <div className="space-y-8">
          {/* Hero-shaped block */}
          <UnifiedCard>
            <div className="flex items-center gap-2 mb-3">
              <Skeleton className="h-6 w-24 rounded-full" />
            </div>
            <Skeleton className="h-4 w-72 mb-5" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Skeleton className="h-32 rounded-xl" />
              <Skeleton className="h-32 rounded-xl" />
            </div>
          </UnifiedCard>
          {/* KPI-grid-shaped block */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-card shadow-sm p-5 space-y-2">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-3 w-36" />
                <Skeleton className="h-9 w-20 mt-1" />
              </div>
            ))}
          </div>
          {/* Benchmark-shaped block */}
          <UnifiedCard icon={BarChart2} title="Benchmark Comparison">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-52 rounded-xl" />
              ))}
            </div>
          </UnifiedCard>
        </div>
      </PageShell>
    );
  }

  if (isError || !data) {
    return (
      <PageShell>
        <div className="space-y-8">
          <UnifiedCard>
            <div className="flex flex-col items-center gap-4 py-12">
              <AlertCircle className="w-8 h-8 text-destructive" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">Failed to load analytics. Please try again.</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
            </div>
          </UnifiedCard>
        </div>
      </PageShell>
    );
  }

  const { summary, benchmarks, offeringBreakdown, studentEngagement } = data;

  const hasOfferings = offeringBreakdown.length > 0
    || summary.oneToOne != null
    || summary.group != null;

  if (!hasOfferings) {
    return (
      <PageShell>
        <div className="space-y-8">
          <div className="rounded-2xl bg-white shadow-md p-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center mx-auto mb-4">
              <BarChart2 className="w-7 h-7 text-slate-400" aria-hidden="true" />
            </div>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto leading-relaxed">
              No analytics data yet. Your stats will appear once your profile is live and students start finding you.
            </p>
          </div>
        </div>
      </PageShell>
    );
  }

  // Which offering types actually have metrics. Both null (with offerings present) is
  // the crash-course-only teacher.
  const presentTypes = (["ONE_TO_ONE", "GROUP"] as const).filter(
    (t) => (t === "ONE_TO_ONE" ? summary.oneToOne : summary.group) != null
  );
  const isCrashCourseOnly = presentTypes.length === 0;

  // Manual choice wins; otherwise fall back to the D5 default. If a manual choice is no
  // longer available (data refetched into a single-type shape) the present type wins.
  const defaultType = computeDefaultOfferingType(summary);
  const candidate = pickedOfferingType ?? defaultType;
  const selectedOfferingType: OfferingTypeKey =
    presentTypes.length === 0 || presentTypes.includes(candidate) ? candidate : presentTypes[0];

  // F17 (AC#28): gate strictly on `summary.benchmarksVisible`, NEVER on
  // `benchmarks.length === 0` -- an empty array is also legitimate for an entitled
  // Gold/Platinum teacher whose cohort is below the minimum size (10). That case is
  // `cohortTooSmallForBenchmarks` below, which explains rather than upsells. Both
  // checks are inert (always false) while the release flag is off, preserving the
  // pre-F17 unconditional rendering.
  const tierBenefitsOn = isReleaseFlagOn('TEACHER_TIER_BENEFITS');
  const benchmarksLocked = tierBenefitsOn && !summary.benchmarksVisible;
  const scopedBenchmarksForSelectedType = benchmarks.filter((b) => b.offeringType === selectedOfferingType);
  const cohortTooSmallForBenchmarks =
    tierBenefitsOn && summary.benchmarksVisible && scopedBenchmarksForSelectedType.length === 0;

  return (
    <PageShell>
      <div className="space-y-8">
        <UnreadInquiriesCallout unreadCount={summary.unreadInquiryCount} />

        <AnalyticsKpiSection
          summary={summary}
          benchmarks={benchmarks}
          selectedOfferingType={selectedOfferingType}
          onSelectOfferingType={setPickedOfferingType}
          showToggle={presentTypes.length === 2}
          isCrashCourseOnly={isCrashCourseOnly}
        />

        <section>
          {benchmarksLocked ? (
            <BenchmarksUpsellCard />
          ) : cohortTooSmallForBenchmarks ? (
            <CohortTooSmallNotice cohortSize={summary.cohortSize} />
          ) : (
            <BenchmarkCharts benchmarks={benchmarks} selectedOfferingType={selectedOfferingType} />
          )}
        </section>

        {!benchmarksLocked && <OfferingBreakdownTable offerings={offeringBreakdown} />}

        <StudentEngagementCard engagement={studentEngagement} />
      </div>
    </PageShell>
  );
}
